import styled from "styled-components";

const HEADER = 30;
const PAD = 8;
const ROW = 17;
const BOX_W = 292;

const heightOf = (n: number) => HEADER + PAD + n * ROW + PAD;

type BoxSpec = {
  id: string;
  x: number;
  y: number;
  title: string;
  href?: string;
  dashed?: boolean;
  properties: string[];
};

const BOXES: BoxSpec[] = [
  {
    id: "DataDesc",
    x: 18,
    y: 18,
    title: "DataDesc",
    href: "/terms/DataDesc",
    properties: ["generalSoftwareInformation", "technicalInterfaceInformation"],
  },
  {
    id: "ERSmeta",
    x: 590,
    y: 18,
    title: "ERSmeta",
    href: "https://github.com/NFDI4Energy/ERSmeta",
    dashed: true,
    properties: ["EnergyResearchSoftware", "license · contact · authors"],
  },
  {
    id: "API",
    x: 18,
    y: 148,
    title: "API",
    href: "/terms/API",
    properties: [
      "dataDescVersion",
      "openapi",
      "info",
      "functions",
      "inputVariables",
      "outputVariables",
      "apiClasses",
      "apiVariables",
      "externalDocs",
    ],
  },
  {
    id: "Function",
    x: 590,
    y: 148,
    title: "Function",
    href: "/terms/Function",
    properties: [
      "identifier",
      "description",
      "deprecated",
      "inputVariables",
      "outputVariables",
    ],
  },
  {
    id: "APIClass",
    x: 18,
    y: 400,
    title: "APIClass",
    href: "/terms/APIClass",
    properties: ["identifier", "description", "classVariables", "functions"],
  },
  {
    id: "Data",
    x: 590,
    y: 322,
    title: "Data",
    href: "/terms/Data",
    properties: [
      "identifier",
      "description",
      "required",
      "deprecated",
      "dataSchema",
    ],
  },
  {
    id: "Info",
    x: 18,
    y: 546,
    title: "Info",
    href: "/terms/Info",
    properties: ["title", "version", "description"],
  },
  {
    id: "DataSchema",
    x: 590,
    y: 496,
    title: "DataSchema",
    href: "/terms/DataSchema",
    properties: [
      "identifier",
      "description",
      "semanticConcept",
      "type",
      "format",
      "properties",
      "dataSchema",
      "dimensions",
      "unit",
      "quantityKind",
      "mediaType",
    ],
  },
  {
    id: "ExternalDocumentation",
    x: 18,
    y: 688,
    title: "ExternalDocumentation",
    href: "/terms/ExternalDocumentation",
    properties: ["description", "url"],
  },
  {
    id: "Dimension",
    x: 590,
    y: 778,
    title: "Dimension",
    href: "/terms/Dimension",
    properties: [
      "identifier",
      "description",
      "minIndex",
      "maxIndex",
      "semanticConcept",
      "unit",
    ],
  },
];

const byId = Object.fromEntries(BOXES.map((b) => [b.id, b]));

const boxH = (id: string) => heightOf(byId[id].properties.length);

const cx = (id: string) => byId[id].x + BOX_W / 2;
const top = (id: string) => byId[id].y;
const bottom = (id: string) => byId[id].y + boxH(id);
const left = (id: string) => byId[id].x;
const right = (id: string) => byId[id].x + BOX_W;
const midY = (id: string) => byId[id].y + boxH(id) / 2;

const SchemaDiagram = () => {
  return (
    <Figure>
      <Scroll>
        <svg
          viewBox="0 0 950 990"
          role="img"
          aria-labelledby="schema-diagram-title schema-diagram-desc"
        >
          <title id="schema-diagram-title">
            DataDesc schema classes and relations
          </title>
          <desc id="schema-diagram-desc">
            A DataDesc document holds ERSmeta general software information and
            an API for the technical interface. The API describes functions,
            classes, and data. Each Data item has a DataSchema, which may nest
            further schemas, object properties, and dimensions.
          </desc>
          <defs>
            <marker
              id="arrow"
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="7"
              markerHeight="7"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#36816a" />
            </marker>
          </defs>

          {/* DataDesc → ERSmeta */}
          <Connector
            d={`M ${right("DataDesc")} ${midY("DataDesc")} H ${left("ERSmeta")}`}
            label="generalSoftwareInformation"
            labelX={454}
            labelY={midY("DataDesc") - 8}
          />
          {/* DataDesc → API */}
          <Connector
            d={`M ${cx("DataDesc")} ${bottom("DataDesc")} V ${top("API")}`}
            label="technicalInterfaceInformation"
            labelX={cx("DataDesc") + 10}
            labelY={(bottom("DataDesc") + top("API")) / 2 + 4}
          />
          {/* API → Function */}
          <Connector
            d={`M ${right("API")} ${top("API") + 78} H ${left("Function")}`}
            label="functions"
            labelX={454}
            labelY={top("API") + 70}
          />
          {/* API / Function → Data */}
          <Connector
            d={`M ${right("API")} ${top("API") + 112} C 470 ${top("API") + 112}, 470 ${midY("Data")}, ${left("Data")} ${midY("Data")}`}
            label="input / output / apiVariables"
            labelX={454}
            labelY={midY("Data") - 10}
          />
          {/* Function → Data */}
          <Connector
            d={`M ${cx("Function")} ${bottom("Function")} V ${top("Data")}`}
            label="input / output"
            labelX={cx("Function") + 10}
            labelY={(bottom("Function") + top("Data")) / 2 + 4}
          />
          {/* API → APIClass */}
          <Connector
            d={`M ${cx("API")} ${bottom("API")} V ${top("APIClass")}`}
            label="apiClasses"
            labelX={cx("API") + 10}
            labelY={(bottom("API") + top("APIClass")) / 2 + 4}
          />
          {/* APIClass → Data */}
          <Connector
            d={`M ${right("APIClass")} ${midY("APIClass")} C 470 ${midY("APIClass")}, 470 ${top("Data") + 24}, ${left("Data")} ${top("Data") + 24}`}
            label="classVariables"
            labelX={454}
            labelY={top("Data") + 16}
          />
          {/* Data → DataSchema */}
          <Connector
            d={`M ${cx("Data")} ${bottom("Data")} V ${top("DataSchema")}`}
            label="dataSchema"
            labelX={cx("Data") + 10}
            labelY={(bottom("Data") + top("DataSchema")) / 2 + 4}
          />
          {/* DataSchema → Dimension */}
          <Connector
            d={`M ${cx("DataSchema")} ${bottom("DataSchema")} V ${top("Dimension")}`}
            label="dimensions"
            labelX={cx("DataSchema") + 10}
            labelY={(bottom("DataSchema") + top("Dimension")) / 2 + 4}
          />
          {/* DataSchema nested */}
          <path
            d={`M ${right("DataSchema") - 8} ${top("DataSchema") + 148} C ${right("DataSchema") + 36} ${top("DataSchema") + 148}, ${right("DataSchema") + 36} ${top("DataSchema") + 188}, ${right("DataSchema") - 8} ${top("DataSchema") + 188}`}
            fill="none"
            stroke="#36816a"
            strokeWidth="1.25"
            markerEnd="url(#arrow)"
          />
          <text
            x={right("DataSchema") + 8}
            y={top("DataSchema") + 172}
            fontSize="9"
            fill="#36816a"
            fontFamily="Archivo, system-ui, sans-serif"
          >
            dataSchema
          </text>
          {/* DataSchema properties → Data */}
          <path
            d={`M ${left("DataSchema")} ${top("DataSchema") + 132} C 520 ${top("DataSchema") + 132}, 520 ${bottom("Data") - 8}, ${left("Data")} ${bottom("Data") - 8}`}
            fill="none"
            stroke="#36816a"
            strokeWidth="1.25"
            markerEnd="url(#arrow)"
          />
          <text
            x={500}
            y={top("DataSchema") + 126}
            fontSize="9"
            fill="#36816a"
            fontFamily="Archivo, system-ui, sans-serif"
            textAnchor="middle"
          >
            properties
          </text>

          {BOXES.map((box) => (
            <SchemaBox key={box.id} box={box} />
          ))}
        </svg>
      </Scroll>
      <Caption>
        Classes of a DataDesc document and the properties that relate them.
        General software metadata follows ERSmeta; the technical interface is an{" "}
        <a href="/terms/API">API</a>. Term definitions are in the{" "}
        <a href="/terms/">vocabulary</a>.
      </Caption>
    </Figure>
  );
};

const Connector = ({
  d,
  label,
  labelX,
  labelY,
}: {
  d: string;
  label: string;
  labelX: number;
  labelY: number;
}) => (
  <g>
    <path
      d={d}
      fill="none"
      stroke="#36816a"
      strokeWidth="1.25"
      markerEnd="url(#arrow)"
    />
    <text
      x={labelX}
      y={labelY}
      fontSize="9"
      fill="#36816a"
      fontFamily="Archivo, system-ui, sans-serif"
      textAnchor="middle"
    >
      {label}
    </text>
  </g>
);

const SchemaBox = ({ box }: { box: BoxSpec }) => {
  const h = heightOf(box.properties.length);
  const headerFill = box.dashed ? "#36816a" : "#323a35";
  const external = box.href?.startsWith("http");

  return (
    <g>
      <rect
        x={box.x}
        y={box.y}
        width={BOX_W}
        height={h}
        rx="7"
        fill="#fff"
        stroke={box.dashed ? "#36816a" : "#323a35"}
        strokeWidth="1.4"
        strokeDasharray={box.dashed ? "5 3.5" : undefined}
      />
      <path
        d={`M ${box.x} ${box.y + 7} a 7 7 0 0 1 7 -7 h ${BOX_W - 14} a 7 7 0 0 1 7 7 v ${HEADER - 7} H ${box.x} z`}
        fill={headerFill}
      />
      {box.href ? (
        <a
          href={box.href}
          target={external ? "_blank" : undefined}
          rel={external ? "noreferrer" : undefined}
        >
          <text
            x={box.x + 12}
            y={box.y + 20}
            fill="#fff"
            fontSize="13"
            fontWeight="600"
            fontFamily="Archivo, system-ui, sans-serif"
          >
            {box.title}
          </text>
        </a>
      ) : (
        <text
          x={box.x + 12}
          y={box.y + 20}
          fill="#fff"
          fontSize="13"
          fontWeight="600"
          fontFamily="Archivo, system-ui, sans-serif"
        >
          {box.title}
        </text>
      )}
      {box.properties.map((prop, i) => (
        <text
          key={prop}
          x={box.x + 12}
          y={box.y + HEADER + PAD + 12 + i * ROW}
          fill="#323a35"
          fontSize="11"
          fontFamily="Archivo, system-ui, sans-serif"
        >
          {prop}
        </text>
      ))}
    </g>
  );
};

const Figure = styled.figure`
  margin: 2rem 0 2.5rem;
  padding: 0;
`;

const Scroll = styled.div`
  width: 100%;
  overflow-x: auto;
  border: 1px solid var(--color-stroke);
  border-radius: 12px;
  background: var(--color-primary-light);
  padding: 12px 8px 4px;

  svg {
    display: block;
    width: 100%;
    min-width: 640px;
    height: auto;
  }

  a {
    color: inherit;
  }

  a:hover text {
    text-decoration: underline;
  }
`;

const Caption = styled.figcaption`
  margin-top: 0.85rem;
  font-size: 0.85rem;
  line-height: 1.55;
  color: var(--color-dark-green);
  font-weight: 300;

  a {
    color: var(--color-dark-green);
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

export default SchemaDiagram;
